import { useCallback, useEffect, useState } from 'react';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { take } from 'lodash';
import { getAllowedParks } from 'routes/OffshoreActivity/OffshoreActivity.utils';
import { useRematchSelector } from 'models/rematchStore';

const groupPrefix = 'GROUP';

const createNewGroup = (groupsSize, checkedItems = []) => ({
    groupId: `${Date.now()}_${groupsSize}`,
    name: `${groupPrefix} ${groupsSize + 1}`,
    checkedItems, // contains park values, eg: GYM, GALLOPER
});

const EMPTY_ARR = [];

export function useGroups() {
    const parkList = useRematchSelector((state) => state.appData.parkList);
    const allowedScreens = useRematchSelector((state) => state.account.allowedScreens);
    const parks = getAllowedParks({ parkList, allowedScreens });
    const selectItemsCount = 3;
    const defaultCheckedItems = take(parks, selectItemsCount).map(({ value }) => value);

    const [groups, setGroups] = useLocalStorage('groups', EMPTY_ARR);

    const [tabIndex, setTabIndex] = useState(0);

    useEffect(() => {
        if (defaultCheckedItems.length > 0 && groups.length === 0) {
            const newGroup = createNewGroup(groups.length, defaultCheckedItems);
            setGroups([...groups, newGroup]);
        }
    }, [defaultCheckedItems, groups, setGroups]);

    const addGroup = useCallback(() => {
        const newGroup = createNewGroup(groups.length);
        setGroups([...groups, newGroup]);
        setTabIndex(groups.length);
    }, [groups, setGroups]);

    const onRemoveGroup = useCallback(
        (groupId, groupIndex) => {
            setGroups([...groups.filter((group) => group.groupId !== groupId)]);
            setTabIndex(Math.max(groupIndex - 1, 0));
        },
        [groups, setGroups]
    );

    const saveGroup = useCallback(
        (groupId, changedGroup) => {
            setGroups(
                groups.map((group) => {
                    if (group.groupId === groupId) {
                        return { ...group, ...changedGroup };
                    }

                    return group;
                })
            );
        },
        [groups, setGroups]
    );

    const selectedGroup = groups[tabIndex];

    const checkedItems = selectedGroup?.checkedItems || [];

    return {
        groups,
        setGroups,
        addGroup,
        onRemoveGroup,
        saveGroup,
        tabIndex,
        setTabIndex,
        selectedGroup,
        checkedItems,
    };
}
